import { z } from 'zod';

export const CIRCLE_MSG_MAX = 300;
export const ROOM_NAME_RE = /^[\p{L}\p{N} '_-]{3,40}$/u;

export const CircleRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** The Spot this room belongs to. */
  spotId: z.string(),
  spotName: z.string(),
  rules: z.string().max(280),
  members: z.number().int().nonnegative(),
  frozen: z.boolean(),
  archived: z.boolean(),
  /** Live and joinable only once an admin has approved the request. */
  approved: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type CircleRoom = z.infer<typeof CircleRoomSchema>;

export const CircleMessageSchema = z.object({
  id: z.string(),
  /** Ephemeral room nickname, e.g. "melon-42". Resets per room join. */
  nick: z.string().max(24),
  flavor: z.enum(['citron', 'peach', 'berry', 'grape', 'lychee', 'melon']),
  text: z.string().max(CIRCLE_MSG_MAX),
  /** True for join/leave/mod system lines. */
  system: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type CircleMessage = z.infer<typeof CircleMessageSchema>;

/* GET /api/circle?spotId=.. */
export const CircleListResponseSchema = z.object({
  rooms: z.array(CircleRoomSchema),
});
export type CircleListResponse = z.infer<typeof CircleListResponseSchema>;

/* GET /api/circle/nearby?lat=&lng= — approved rooms across nearby Spots. */
export const NearbyCirclesResponseSchema = z.object({
  rooms: z.array(CircleRoomSchema),
});
export type NearbyCirclesResponse = z.infer<typeof NearbyCirclesResponseSchema>;

/* POST /api/circle — request a new room; goes live once an admin approves it. */
export const CreateCircleSchema = z.object({
  spotId: z.string(),
  name: z.string().regex(ROOM_NAME_RE),
  rules: z.string().max(280).default('Be kind. No numbers, no meetups pushed, no hate.'),
});
export type CreateCircle = z.infer<typeof CreateCircleSchema>;

/* GET /api/circle/:id */
export const CircleRoomResponseSchema = z.object({
  room: CircleRoomSchema,
  /** Your ephemeral nickname for this room. */
  nick: z.string(),
  recent: z.array(CircleMessageSchema),
});
export type CircleRoomResponse = z.infer<typeof CircleRoomResponseSchema>;
